import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { ProductSettingsModel, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import ProductAvailability, { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import XCircle from "@insite/mobius/Icons/XCircle";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import parse from "html-react-parser";
import React, { FC } from "react";
import { connect, HandleThunkActionCreator } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions: PromotionModel[];
    productSettings: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: HandleThunkActionCreator<typeof removeCartLine>;
    showRemoveAction?: boolean;
    extendedStyles?: CartLineCardCondensedStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
});

type Props = OwnProps & HasCartLineContext & ReturnType<typeof mapStateToProps>;

export interface CartLineCardCondensedStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    cartLineInfoGridItem?: GridItemProps;
    cartLineInfoContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productNumberGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    productErpNumberText?: TypographyProps;
    productPriceAndQuantityGridItem?: GridItemProps;
    productPriceAndQuantityContainer?: GridContainerProps;
    productPriceAndAvailabilityGridItem?: GridItemProps;
    productPrice?: ProductPriceStyles;
    productAvailability?: ProductAvailabilityStyles;
    cartLineErrorMessageText?: TypographyProps;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    smQuantity?: CartLineQuantityStyles;
    removeCartLineGridItem?: GridItemProps;
    removeCartLineIcon?: IconPresentationProps;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPriceText?: TypographyProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    priceText?: TypographyPresentationProps;
    priceLabelText?: TypographyPresentationProps;
    wrapper?: any;
    priceWrapper?: InjectableCss;
    erpNumberLabelText?: TypographyPresentationProps;
    htmlContent?: TypographyPresentationProps;
    productInfoPartNumberHeadingAndText?: SmallHeadingAndTextStyles;
    hiddenBelowMd?: HiddenProps;
    hiddenAboveSm?: HiddenProps;
    smContainerGridItem?: GridItemProps;
    smTopContainer?: GridContainerProps;
    smProductImageGridItem?: GridItemProps;
    smProductBrandAndDescriptionGridItem?: GridItemProps;
    smRemoveCartLineGridItem?: GridItemProps;
    smCartLineInfoGridItem?: GridItemProps;
    smProductPriceAndQuantityContainer?: GridContainerProps;
}

export const cartLineCardCondensedStyles: CartLineCardCondensedStyles = {
    hiddenBelowMd: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    hiddenAboveSm: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            align-items: flex-start;
        `,
    },
    smContainerGridItem: { width: 12 },
    smTopContainer: { gap: 10 },
    smProductImageGridItem: {
        width: 2,
        css: css`
            font-size: 10px;
        `,
    },
    smProductBrandAndDescriptionGridItem: {
        width: 8,
        css: css`
            flex-direction: column;
            padding-bottom: 10px;
        `,
    },
    smRemoveCartLineGridItem: {
        width: 2,
        css: css`
            justify-content: center;
        `,
    },
    smCartLineInfoGridItem: { width: 12 },
    smProductPriceAndQuantityContainer: {
        css: css`
            margin: 14px 0 0 30px;
        `,
    },
    smQuantity: {
        editableQuantityTextField: {
            mergeCss: true,
            labelProps: {
                css: css`
                    font-size: 14px;
                    font-weight: bold;
                    ${({ theme }) => {
                        return breakpointMediaQueries(theme, [
                            css`
                                text-align: left;
                            `,
                            css`
                                text-align: left;
                            `,
                            css`
                                text-align: left;
                            `,
                            css`
                                text-align: center;
                            `,
                            css`
                                text-align: center;
                            `,
                        ]);
                    }}
                `,
            },
            cssOverrides: {
                formInputWrapper: css`
                    margin-top: 0px !important;
                    width: 70%;
                `,
            },
        },
    },
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: 2,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    null,
                ])}
        `,
    },
    cartLineInfoGridItem: { width: [8, 8, 8, 9, 9] },
    productBrandAndDescriptionGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            padding-bottom: 10px;
        `,
    },
    productNumberGridItem: {
        width: [6, 6, 6, 3, 3],
        css: css`
            flex-direction: row;
            padding-top: 0px;
        `,
    },
    productPriceAndAvailabilityGridItem: {
        width: [6, 6, 6, 3, 3],
        css: css`
            flex-direction: column;
            padding-top: 0px;
        `,
    },
    erpNumberLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
            margin-right: 15px;
        `,
    },
    productErpNumberText: {
        css: css`
            width: 100%;
            word-wrap: break-word;
        `,
    },
    productPriceAndQuantityGridItem: {
        width: 12,
        css: css`
            & ${GridOffset} {
                justify-content: space-between;
            }
        `,
    },
    productPriceAndQuantityContainer: {
        css: css`
            justify-content: flex-end;
        `,
    },
    cartLineErrorMessageText: { color: "danger" },
    quantityGridItem: {
        css: css`
            padding-top: 0px;
            vertical-align: top;
            display: flex;
        `,
        width: [6, 6, 6, 3, 2],
    },
    quantity: {
        editableQuantityTextField: {
            mergeCss: true,
            labelProps: {
                css: css`
                    font-size: 14px;
                    font-weight: bold;
                    ${({ theme }) => {
                        return breakpointMediaQueries(theme, [
                            css`
                                text-align: left;
                            `,
                            css`
                                text-align: left;
                            `,
                            css`
                                text-align: left;
                            `,
                            css`
                                text-align: center;
                            `,
                            css`
                                text-align: center;
                            `,
                        ]);
                    }}
                `,
            },
            cssOverrides: {
                formInputWrapper: css`
                    margin-top: 0px !important;
                `,
            },
        },
    },
    removeCartLineGridItem: {
        css: css`
            justify-content: center;
        `,
        width: [2, 2, 2, 1, 1],
    },
    removeCartLineIcon: { src: XCircle },
    extendedUnitNetPriceGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-end;
        `,
    },
    extendedUnitNetPriceText: {
        weight: "bold",
    },
    priceWrapper: {
        css: css`
            margin-top: 10px;
        `,
    },
    priceLabelText: {
        variant: "legend",
        css: css`
            font-weight: bold;
            font-size: 14px;
        `,
    },
    htmlContent: {
        css: css`
            padding: 5px;
            box-shadow: 0px 0px 4px 2px rgb(0 0 0 / 20%);
            background-color: rgb(251 241 200);
            p {
                margin-bottom: 0rem;
            }
        `,
    },
    priceText: {
        weight: "normal",
    },
    productPrice: {
        priceLabelText: {
            css: css`
                font-weight: bold;
                font-size: 14px;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            text-align: left;
                        `,
                        css`
                            text-align: left;
                        `,
                        css`
                            text-align: left;
                        `,
                        css`
                            text-align: center;
                        `,
                        css`
                            text-align: center;
                        `,
                    ]);
                }}
            `,
        },
        priceWrapper: {
            css: css`
                margin-top: 10px;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            text-align: left;
                        `,
                        css`
                            text-align: left;
                        `,
                        css`
                            text-align: left;
                        `,
                        css`
                            text-align: center;
                        `,
                        css`
                            text-align: center;
                        `,
                    ]);
                }}
            `,
        },
        price: {
            priceText: {
                weight: "normal",
            },
        },
    },
    productInfoPartNumberHeadingAndText: {
        extendedStyles: {
            heading: {
                css: css`
                    font-size: 14px;
                    font-weight: bold;
                `,
            },
            text: {
                css: css`
                    margin-top: 10px;
                    text-align: left;
                    width: 100%;
                `,
            },
        },
    },
};

const SectionWrapper = getStyledWrapper("section");

const CartLineCardCondensed: FC<Props> = ({
    cart,
    cartLine,
    showInventoryAvailability,
    removeCartLine,
    updateCartLine,
    extendedStyles,
    showRemoveAction,
    enableVat,
    vatPriceDisplay,
}) => {
    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                removeCartLine({ cartLineId: cartLine.id });
            } else {
                updateCartLine({
                    cartLine: {
                        ...cartLine,
                        qtyOrdered,
                    },
                });
            }
        }
    };

    const renderCustomProperty = (value: string) => {
        if (value !== undefined) {
            let customContent = value;
            if (customContent.includes("<=")) {
                customContent = customContent.replace(/<=/g, "&lt=");
            }
            if (customContent.includes("=<")) {
                customContent = customContent.replace(/=</g, "=&lt");
            }
            if (customContent.includes(">=")) {
                customContent = customContent.replace(/>=/g, "&gt=");
            }
            if (customContent.includes("=>")) {
                customContent = customContent.replace(/=>/g, "=&gt");
            }
            return <Typography {...styles.htmlContent}>{parse(customContent, parserOptions)}</Typography>;
        }
    };

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction) {
            return;
        }

        removeCartLine({ cartLineId: cartLine.id });
    };

    const sumQtyPerUom = cart.cartLines!.reduce((sum, current) => {
        return current.productId === cartLine.productId
            ? sum + current.qtyPerBaseUnitOfMeasure * current.qtyOrdered!
            : sum;
    }, 0);
    const errorMessages: React.ReactNode[] = [];
    if (showInventoryAvailability && cartLine.hasInsufficientInventory && !isOutOfStock(cartLine)) {
        const tooManyRequestedMessage = siteMessage(
            "Cart_ToManyQtyRequested",
            cartLine.qtyOnHand.toLocaleString(),
            sumQtyPerUom.toLocaleString(),
        );
        errorMessages.push(tooManyRequestedMessage);
    }

    if (cartLine.isRestricted) {
        errorMessages.push(translate("Restricted product"));
    }

    if (!cartLine.isActive) {
        errorMessages.push(translate("Inactive Product"));
    }

    if (cartLine.cantBuy) {
        errorMessages.push(translate("View-only product"));
    }

    const [styles] = React.useState(() => mergeToNew(cartLineCardCondensedStyles, extendedStyles));
    const displayWithVat = vatPriceDisplay === "DisplayWithVat" || vatPriceDisplay === "DisplayWithAndWithoutVat";

    return (
        <GridContainer
            {...styles.container}
            data-test-selector={`cartline_condensed_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            <Hidden above="sm" {...styles.hiddenAboveSm}>
                <GridItem {...styles.smProductImageGridItem}>
                    <ProductImage product={cartLine} extendedStyles={styles.productImage} />
                </GridItem>
                <GridItem {...styles.smProductBrandAndDescriptionGridItem}>
                    <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                </GridItem>
                <GridItem {...styles.smRemoveCartLineGridItem}>
                    {showRemoveAction && (
                        <Clickable onClick={removeCartLineClickHandler} data-test-selector="cartline_removeLine">
                            <VisuallyHidden>{translate("Remove item")}</VisuallyHidden>
                            <IconMemo {...styles.removeCartLineIcon} />
                        </Clickable>
                    )}
                </GridItem>
                <GridItem {...styles.smCartLineInfoGridItem}>
                    <GridContainer {...styles.cartLineInfoContainer}>
                        <GridItem {...styles.productPriceAndQuantityGridItem}>
                            <GridContainer {...styles.smProductPriceAndQuantityContainer}>
                                <GridItem {...styles.productNumberGridItem}>
                                    <SmallHeadingAndText
                                        {...styles.productInfoPartNumberHeadingAndText}
                                        heading={translate("Part #")}
                                        text={`${cartLine.erpNumber}`}
                                    />
                                </GridItem>
                                <GridItem {...styles.productPriceAndAvailabilityGridItem}>
                                    {!cart.cartNotPriced && (
                                        <ProductPrice
                                            product={cartLine}
                                            currencySymbol={cart.currencySymbol}
                                            showSavings={false}
                                            showInvalidPriceMessage={true}
                                            extendedStyles={styles.productPrice}
                                        />
                                    )}
                                    {showInventoryAvailability && !cartLine.quoteRequired && (
                                        <ProductAvailability
                                            productId={cartLine.productId!}
                                            availability={cartLine.availability!}
                                            unitOfMeasure={cartLine.unitOfMeasure}
                                            trackInventory={cartLine.trackInventory}
                                            failedToLoadInventory={cart.failedToGetRealTimeInventory}
                                            extendedStyles={styles.productAvailability}
                                        />
                                    )}
                                    {errorMessages.length > 0 &&
                                        errorMessages.map((message, index) => (
                                            <Typography
                                                {...styles.cartLineErrorMessageText}
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={index}
                                                data-test-selector="cartline_errorMessage"
                                            >
                                                {message}
                                            </Typography>
                                        ))}
                                </GridItem>
                                <GridItem {...styles.quantityGridItem}>
                                    <CartLineQuantity
                                        cart={cart}
                                        editable={true}
                                        onQtyOrderedChange={qtyOrderedChangeHandler}
                                        extendedStyles={styles.smQuantity}
                                    />
                                </GridItem>
                                <GridItem {...styles.quantityGridItem}>
                                    {!cartLine.quoteRequired &&
                                        !cart.cartNotPriced &&
                                        cartLine.pricing &&
                                        cartLine.pricing.extendedUnitNetPrice > 0 && (
                                            <SectionWrapper {...styles.wrapper}>
                                                <Typography
                                                    {...styles.priceLabelText}
                                                    data-test-selector="cartline_extendedUnitNetPriceLabel"
                                                >
                                                    {translate("Total")}
                                                </Typography>
                                                <StyledWrapper {...styles.priceWrapper}>
                                                    <Typography
                                                        {...styles.priceText}
                                                        data-test-selector="cartline_extendedUnitNetPrice"
                                                    >
                                                        {enableVat && displayWithVat
                                                            ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                                            : cartLine.pricing.extendedUnitNetPriceDisplay}
                                                    </Typography>
                                                </StyledWrapper>
                                            </SectionWrapper>
                                        )}
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                        {cartLine.properties["cartNotes"] !== undefined &&
                            cartLine.properties["cartNotes"].length > 0 && (
                                <GridItem {...styles.productBrandAndDescriptionGridItem}>
                                    {renderCustomProperty(cartLine.properties["cartNotes"])}
                                </GridItem>
                            )}
                    </GridContainer>
                </GridItem>
            </Hidden>
            <Hidden below="md" {...styles.hiddenBelowMd}>
                <GridItem {...styles.productImageGridItem}>
                    <ProductImage product={cartLine} extendedStyles={styles.productImage} />
                </GridItem>
                <GridItem {...styles.cartLineInfoGridItem}>
                    <GridContainer {...styles.cartLineInfoContainer}>
                        <GridItem {...styles.productBrandAndDescriptionGridItem}>
                            <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                        </GridItem>
                        <GridItem {...styles.productPriceAndQuantityGridItem}>
                            <GridContainer {...styles.productPriceAndQuantityContainer}>
                                <GridItem {...styles.productNumberGridItem}>
                                    <SmallHeadingAndText
                                        {...styles.productInfoPartNumberHeadingAndText}
                                        heading={translate("Part #")}
                                        text={`${cartLine.erpNumber}`}
                                    />
                                </GridItem>
                                <GridItem {...styles.productPriceAndAvailabilityGridItem}>
                                    {!cart.cartNotPriced && (
                                        <ProductPrice
                                            product={cartLine}
                                            currencySymbol={cart.currencySymbol}
                                            showSavings={false}
                                            showInvalidPriceMessage={true}
                                            extendedStyles={styles.productPrice}
                                        />
                                    )}
                                    {showInventoryAvailability && !cartLine.quoteRequired && (
                                        <ProductAvailability
                                            productId={cartLine.productId!}
                                            availability={cartLine.availability!}
                                            unitOfMeasure={cartLine.unitOfMeasure}
                                            trackInventory={cartLine.trackInventory}
                                            failedToLoadInventory={cart.failedToGetRealTimeInventory}
                                            extendedStyles={styles.productAvailability}
                                        />
                                    )}
                                    {errorMessages.length > 0 &&
                                        errorMessages.map((message, index) => (
                                            <Typography
                                                {...styles.cartLineErrorMessageText}
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={index}
                                                data-test-selector="cartline_errorMessage"
                                            >
                                                {message}
                                            </Typography>
                                        ))}
                                </GridItem>
                                <GridItem {...styles.quantityGridItem}>
                                    <CartLineQuantity
                                        cart={cart}
                                        editable={true}
                                        onQtyOrderedChange={qtyOrderedChangeHandler}
                                        extendedStyles={styles.quantity}
                                    />
                                </GridItem>
                                <GridItem {...styles.quantityGridItem}>
                                    {!cartLine.quoteRequired &&
                                        !cart.cartNotPriced &&
                                        cartLine.pricing &&
                                        cartLine.pricing.extendedUnitNetPrice > 0 && (
                                            <SectionWrapper {...styles.wrapper}>
                                                <Typography
                                                    {...styles.priceLabelText}
                                                    data-test-selector="cartline_extendedUnitNetPriceLabel"
                                                >
                                                    {translate("Total")}
                                                </Typography>
                                                <StyledWrapper {...styles.priceWrapper}>
                                                    <Typography
                                                        {...styles.priceText}
                                                        data-test-selector="cartline_extendedUnitNetPrice"
                                                    >
                                                        {enableVat && displayWithVat
                                                            ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                                            : cartLine.pricing.extendedUnitNetPriceDisplay}
                                                    </Typography>
                                                </StyledWrapper>
                                            </SectionWrapper>
                                        )}
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                        {cartLine.properties["cartNotes"] !== undefined &&
                            cartLine.properties["cartNotes"].length > 0 && (
                                <GridItem {...styles.productBrandAndDescriptionGridItem}>
                                    {renderCustomProperty(cartLine.properties["cartNotes"])}
                                </GridItem>
                            )}
                    </GridContainer>
                </GridItem>
                <GridItem {...styles.removeCartLineGridItem}>
                    {showRemoveAction && (
                        <Clickable onClick={removeCartLineClickHandler} data-test-selector="cartline_removeLine">
                            <VisuallyHidden>{translate("Remove item")}</VisuallyHidden>
                            <IconMemo {...styles.removeCartLineIcon} />
                        </Clickable>
                    )}
                </GridItem>
            </Hidden>
        </GridContainer>
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineCardCondensed));
